import { Row, Col, Form } from "react-bootstrap";
import { UPLOADS_BASE, MEDICAL_SPECIALTY_TYPE_OPTIONS, MVT_RELEVANCE_OPTIONS } from "../constants/api";
import { parseImagePaths } from "../utils/helpers";
import { filterParentLocations } from "../../../utils/locationHelpers";

const UserTypeMasterForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="userType">
      <Form.Label>User Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Doctor, Hospital, Patient"
        value={formData.user_type || ""}
        onChange={(e) => onChange("user_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Location Type Master ────────────────────────────────────────────────────

const LocationTypeMasterForm = ({ formData, onChange }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="locationTypeName">
        <Form.Label>Location Type</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Country, State, City, Region"
          value={formData.location_type || ""}
          onChange={(e) => onChange("location_type", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Location Master ─────────────────────────────────────────────────────────

const LOCATION_CATEGORIES = ["Referral Location", "Treatment Location"];
const LOCATION_CURRENCIES = ["USD", "INR", "EUR"];

const LocationMasterForm = ({ formData, onChange, locationTypes, locations }) => {
  const parentLocations = filterParentLocations(
    locations,
    locationTypes,
    formData.location_type_id
  );
  const isChildLocation = !!formData.parent_location_id;
  const inheritedFieldStyle = isChildLocation
    ? { background: "#f4f7fb", cursor: "not-allowed" }
    : {};

  return (
    <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="locationTypeIdRef">
        <Form.Label>Location Type</Form.Label>
        <Form.Select
          required
          value={formData.location_type_id || ""}
          onChange={(e) => onChange("location_type_id", e.target.value)}
        >
          <option value="">Select location type</option>
          {locationTypes.map((lt) => (
            <option key={lt.location_type_id} value={lt.location_type_id}>
              {lt.location_type}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="parentLocationId">
        <Form.Label>Parent Location</Form.Label>
        <Form.Select
          value={formData.parent_location_id || ""}
          onChange={(e) => onChange("parent_location_id", e.target.value)}
        >
          <option value="">None (Top-level)</option>
          {parentLocations.map((loc) => (
            <option key={loc.location_id} value={loc.location_id}>
              {loc.location_name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="locationCategory">
        <Form.Label>Location Category</Form.Label>
        <Form.Select
          required
          value={formData.location_category || ""}
          onChange={(e) => onChange("location_category", e.target.value)}
        >
          <option value="">Select location category</option>
          {LOCATION_CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="locationName">
        <Form.Label>Location Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter location name"
          required
          value={formData.location_name || ""}
          onChange={(e) => onChange("location_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="countryCode">
        <Form.Label>Country Code</Form.Label>
        <Form.Control
          type="text"
          placeholder={isChildLocation ? "Auto-filled from parent" : "e.g. IN, US"}
          maxLength={10}
          value={formData.country_code || ""}
          onChange={(e) => onChange("country_code", e.target.value)}
          readOnly={isChildLocation}
          style={inheritedFieldStyle}
        />
        {isChildLocation && (
          <Form.Text className="text-muted">Inherited from parent location</Form.Text>
        )}
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="locationIconImage">
        <Form.Label>Icon Image</Form.Label>
        <Form.Control
          type="file"
          accept="image/*"
          onChange={(e) => onChange("icon_image", e.target.files[0])}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="treatmentCostCurrency">
        <Form.Label>Treatment Cost Currency</Form.Label>
        <Form.Select
          value={formData.treatment_cost_currency || ""}
          onChange={(e) => onChange("treatment_cost_currency", e.target.value)}
          disabled={isChildLocation}
          style={inheritedFieldStyle}
        >
          <option value="">{isChildLocation ? "Auto-filled from parent" : "Select currency"}</option>
          {LOCATION_CURRENCIES.map((currency) => (
            <option key={currency} value={currency}>{currency}</option>
          ))}
        </Form.Select>
        {isChildLocation && (
          <Form.Text className="text-muted">Inherited from country level</Form.Text>
        )}
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="localCurrency">
        <Form.Label>Local Currency</Form.Label>
        <Form.Select
          value={formData.local_currency || ""}
          onChange={(e) => onChange("local_currency", e.target.value)}
          disabled={isChildLocation}
          style={inheritedFieldStyle}
        >
          <option value="">{isChildLocation ? "Auto-filled from parent" : "Select currency"}</option>
          {LOCATION_CURRENCIES.map((currency) => (
            <option key={currency} value={currency}>{currency}</option>
          ))}
        </Form.Select>
        {isChildLocation && (
          <Form.Text className="text-muted">Inherited from country level</Form.Text>
        )}
      </Form.Group>
    </Row>
    <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="locationCountryFlagImage">
        <Form.Label>Country Flag Image</Form.Label>
        {isChildLocation ? (
          <>
            {formData.country_flag_image_path ? (
              <div className="mt-1">
                <img
                  src={`${UPLOADS_BASE}/uploads/${formData.country_flag_image_path}`}
                  alt="Country flag"
                  style={{ maxWidth: 80, maxHeight: 48, objectFit: "contain", borderRadius: 4 }}
                />
              </div>
            ) : (
              <Form.Control
                type="text"
                readOnly
                value="No flag on parent"
                style={{ background: "#f4f7fb", cursor: "not-allowed" }}
              />
            )}
            <Form.Text className="text-muted">Inherited from parent location</Form.Text>
          </>
        ) : (
          <Form.Control
            type="file"
            accept="image/*"
            onChange={(e) => onChange("country_flag_image", e.target.files[0])}
          />
        )}
      </Form.Group>
    </Row>
    </>
  );
};

// ─── Medical Specialty Master ────────────────────────────────────────────────

const MedicalSpecialtyForm = ({ formData, onChange }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="medicalSpecialty">
        <Form.Label>Medical Specialty</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Cardiology, Dermatology"
          value={formData.medical_specialty || ""}
          onChange={(e) => onChange("medical_specialty", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="medicalSpecialtyType">
        <Form.Label>Medical Specialty Type</Form.Label>
        <Form.Select
          required
          value={formData.medical_specialty_type || ""}
          onChange={(e) => onChange("medical_specialty_type", e.target.value)}
        >
          <option value="">Select type</option>
          {MEDICAL_SPECIALTY_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4 mt-3">
      <Form.Group as={Col} md="6" controlId="mvtRelevance">
        <Form.Label>MVT Relevance</Form.Label>
        <Form.Select
          required
          value={formData.mvt_relevance || ""}
          onChange={(e) => onChange("mvt_relevance", e.target.value)}
        >
          <option value="">Select Yes/No</option>
          {MVT_RELEVANCE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="medicalSpecialtyIconImage">
        <Form.Label>Icon Image</Form.Label>
        <Form.Control
          type="file"
          accept="image/*"
          onChange={(e) => onChange("icon_image", e.target.files[0])}
        />
      </Form.Group>
    </Row>
  </>
);

// ─── Medical Procedure Master ────────────────────────────────────────────────

const MedicalProcedureForm = ({ formData, onChange, specialties }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="medicalSpecialtyId">
        <Form.Label>Medical Specialty</Form.Label>
        <Form.Select
          required
          value={formData.medical_specialty_id || ""}
          onChange={(e) => onChange("medical_specialty_id", e.target.value)}
        >
          <option value="">Select medical specialty</option>
          {specialties.map((s) => (
            <option key={s.medical_specialty_id} value={s.medical_specialty_id}>
              {s.medical_specialty}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="procedureId">
        <Form.Label>Procedure ID</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. PROC-001"
          value={formData.procedure_id || ""}
          onChange={(e) => onChange("procedure_id", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="procedureName">
        <Form.Label>Procedure Name</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Angioplasty, MRI Scan"
          value={formData.procedure_name || ""}
          onChange={(e) => onChange("procedure_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="alternateName">
        <Form.Label>Alternate Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Alternative procedure name"
          value={formData.alternate_name || ""}
          onChange={(e) => onChange("alternate_name", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="surgicalApproach">
        <Form.Label>Surgical Approach</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Open, Laparoscopic, Endoscopic"
          value={formData.surgical_approach || ""}
          onChange={(e) => onChange("surgical_approach", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="complexityLevel">
        <Form.Label>Complexity Level</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Low, Moderate, High"
          value={formData.complexity_level || ""}
          onChange={(e) => onChange("complexity_level", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="icd10PcsCode">
        <Form.Label>ICD-10-PCS Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. 02703DZ"
          value={formData.icd10_pcs_code || ""}
          onChange={(e) => onChange("icd10_pcs_code", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="cptReferenceCode">
        <Form.Label>CPT Reference Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. 92928"
          value={formData.cpt_reference_code || ""}
          onChange={(e) => onChange("cpt_reference_code", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="procedureClinicalNotes">
        <Form.Label>Clinical Notes</Form.Label>
        <Form.Control
          type="text"
          placeholder="Additional clinical notes"
          value={formData.clinical_notes || ""}
          onChange={(e) => onChange("clinical_notes", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="pastSurgeryRelevance">
        <Form.Label>Past Surgery Relevance</Form.Label>
        <YesNoRadio
          idPrefix="pastSurgeryRelevance"
          value={formData.past_surgery_relevance ?? 0}
          onChange={(v) => onChange("past_surgery_relevance", v)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="procedureMvtRelevance">
        <Form.Label>MVT Relevance</Form.Label>
        <Form.Select
          required
          value={formData.mvt_relevance || ""}
          onChange={(e) => onChange("mvt_relevance", e.target.value)}
        >
          <option value="">Select Yes/No</option>
          {MVT_RELEVANCE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="3" controlId="estCostUsdLow">
        <Form.Label>Est. Cost USD (Low)</Form.Label>
        <Form.Control
          type="number"
          min="0"
          step="0.01"
          placeholder="e.g. 5500"
          value={formData.est_cost_usd_low ?? ""}
          onChange={(e) => onChange("est_cost_usd_low", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="3" controlId="estCostUsdHigh">
        <Form.Label>Est. Cost USD (High)</Form.Label>
        <Form.Control
          type="number"
          min="0"
          step="0.01"
          placeholder="e.g. 8000"
          value={formData.est_cost_usd_high ?? ""}
          onChange={(e) => onChange("est_cost_usd_high", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="3" controlId="estCostInrLow">
        <Form.Label>Est. Cost INR (Low)</Form.Label>
        <Form.Control
          type="number"
          min="0"
          step="0.01"
          placeholder="e.g. 522500"
          value={formData.est_cost_inr_low ?? ""}
          onChange={(e) => onChange("est_cost_inr_low", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="3" controlId="estCostInrHigh">
        <Form.Label>Est. Cost INR (High)</Form.Label>
        <Form.Control
          type="number"
          min="0"
          step="0.01"
          placeholder="e.g. 760000"
          value={formData.est_cost_inr_high ?? ""}
          onChange={(e) => onChange("est_cost_inr_high", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

// ─── Disease Master ────────────────────────────────────────────────────────────

const YesNoRadio = ({ idPrefix, value, onChange, required }) => (
  <div className="d-flex gap-3 mt-1">
    <Form.Check
      type="radio"
      id={`${idPrefix}Yes`}
      name={idPrefix}
      label="Yes"
      required={required}
      checked={value === 1}
      onChange={() => onChange(1)}
    />
    <Form.Check
      type="radio"
      id={`${idPrefix}No`}
      name={idPrefix}
      label="No"
      checked={value === 0}
      onChange={() => onChange(0)}
    />
  </div>
);

const YesNoRadioInline = ({ name, value, onChange }) => (
  <div className="d-flex gap-2 flex-wrap">
    <Form.Check
      type="radio"
      id={`${name}Yes`}
      name={name}
      label="Yes"
      checked={value === 1}
      onChange={() => onChange(1)}
    />
    <Form.Check
      type="radio"
      id={`${name}No`}
      name={name}
      label="No"
      checked={value === 0}
      onChange={() => onChange(0)}
    />
  </div>
);

const formatYesNo = (val) => (val === 1 ? "Yes" : val === 0 ? "No" : "—");

const DiseaseForm = ({ formData, onChange, specialties }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="diseaseMedicalSpecialtyId">
        <Form.Label>Medical Specialty</Form.Label>
        <Form.Select
          required
          value={formData.medical_specialty_id || ""}
          onChange={(e) => onChange("medical_specialty_id", e.target.value)}
        >
          <option value="">Select medical specialty</option>
          {specialties.map((s) => (
            <option key={s.medical_specialty_id} value={s.medical_specialty_id}>
              {s.medical_specialty}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="diseaseName">
        <Form.Label>Disease Name</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Coronary Artery Disease, Hypertension"
          value={formData.disease_name || ""}
          onChange={(e) => onChange("disease_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="diseaseDescription">
        <Form.Label>Description</Form.Label>
        <Form.Control
          type="text"
          placeholder="Brief description of the disease"
          value={formData.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="comorbidityDisease">
        <Form.Label>Comorbidity Disease</Form.Label>
        <YesNoRadio
          required
          idPrefix="comorbidityDisease"
          value={formData.comorbidity_disease}
          onChange={(v) => onChange("comorbidity_disease", v)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="familyHistoryDisease">
        <Form.Label>Family History Disease</Form.Label>
        <YesNoRadio
          required
          idPrefix="familyHistoryDisease"
          value={formData.family_history_disease}
          onChange={(v) => onChange("family_history_disease", v)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="pastIllnessDisease">
        <Form.Label>Past Illness Disease</Form.Label>
        <YesNoRadio
          required
          idPrefix="pastIllnessDisease"
          value={formData.past_illness_disease}
          onChange={(v) => onChange("past_illness_disease", v)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="icd10Code">
        <Form.Label>ICD10 Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. I25.10"
          value={formData.icd10_code || ""}
          onChange={(e) => onChange("icd10_code", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="icd11Code">
        <Form.Label>ICD11 Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. BA40"
          value={formData.icd11_code || ""}
          onChange={(e) => onChange("icd11_code", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

// ─── Symptom Category Master ───────────────────────────────────────────────────

const SymptomCategoryForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="symptomCategory">
      <Form.Label>Symptom Category</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. General, Respiratory, Neurological"
        value={formData.symptom_category || ""}
        onChange={(e) => onChange("symptom_category", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Symptom Master ────────────────────────────────────────────────────────────

const SymptomForm = ({ formData, onChange, symptomCategories }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="symptomCategoryId">
        <Form.Label>Symptom Category</Form.Label>
        <Form.Select
          required
          value={formData.symptom_category_id || ""}
          onChange={(e) => onChange("symptom_category_id", e.target.value)}
        >
          <option value="">Select symptom category</option>
          {symptomCategories.map((sc) => (
            <option key={sc.symptom_category_id} value={sc.symptom_category_id}>
              {sc.symptom_category}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="symptomName">
        <Form.Label>Symptom Name</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Fever, Headache, Cough"
          value={formData.symptom_name || ""}
          onChange={(e) => onChange("symptom_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="symptomCommonName">
        <Form.Label>Common Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. High temperature"
          value={formData.common_name || ""}
          onChange={(e) => onChange("common_name", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="symptomSeverityLevel">
        <Form.Label>Severity Level</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Mild, Moderate, Severe"
          value={formData.severity_level || ""}
          onChange={(e) => onChange("severity_level", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="symptomAcuteChronic">
        <Form.Label>Acute / Chronic</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Acute, Chronic"
          value={formData.acute_chronic || ""}
          onChange={(e) => onChange("acute_chronic", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="symptomBodySystem">
        <Form.Label>Body System</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Respiratory, Nervous"
          value={formData.body_system || ""}
          onChange={(e) => onChange("body_system", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="symptomIcd10Reference">
        <Form.Label>ICD-10 Reference</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. R50.9"
          value={formData.icd10_reference || ""}
          onChange={(e) => onChange("icd10_reference", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="symptomClinicalNotes">
        <Form.Label>Clinical Notes</Form.Label>
        <Form.Control
          type="text"
          placeholder="Additional clinical notes"
          value={formData.clinical_notes || ""}
          onChange={(e) => onChange("clinical_notes", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="symptomDescription">
        <Form.Label>Description</Form.Label>
        <Form.Control
          type="text"
          placeholder="Brief description of the symptom"
          value={formData.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="symptomPicture">
        <Form.Label>Associated Picture</Form.Label>
        <Form.Control
          type="file"
          accept="image/*"
          onChange={(e) => onChange("picture", e.target.files[0])}
        />
      </Form.Group>
    </Row>
  </>
);

// ─── Aggravating Factor Master ─────────────────────────────────────────────────

const AggravatingFactorForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="aggravatingFactor">
      <Form.Label>Aggravating Factor</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Physical exertion, Cold weather, Stress"
        value={formData.aggravating_factor || ""}
        onChange={(e) => onChange("aggravating_factor", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Allergy Type Master ───────────────────────────────────────────────────────

const AllergyTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="allergyType">
      <Form.Label>Allergy Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Food, Drug, Environmental, Insect"
        value={formData.allergy_type || ""}
        onChange={(e) => onChange("allergy_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Allergy Master ────────────────────────────────────────────────────────────

const AllergyForm = ({ formData, onChange, allergyTypes }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="allergyTypeId">
        <Form.Label>Allergy Type</Form.Label>
        <Form.Select
          required
          value={formData.allergy_type_id || ""}
          onChange={(e) => onChange("allergy_type_id", e.target.value)}
        >
          <option value="">Select allergy type</option>
          {allergyTypes.map((at) => (
            <option key={at.allergy_type_id} value={at.allergy_type_id}>
              {at.allergy_type}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="allergyName">
        <Form.Label>Allergy</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Peanuts, Penicillin, Pollen"
          value={formData.allergy || ""}
          onChange={(e) => onChange("allergy", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Trauma Type Master ────────────────────────────────────────────────────────

const TraumaTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="traumaType">
      <Form.Label>Trauma Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Blunt, Penetrating, Burn, Chemical"
        value={formData.trauma_type || ""}
        onChange={(e) => onChange("trauma_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Trauma Master ─────────────────────────────────────────────────────────────

const TraumaForm = ({ formData, onChange, traumaTypes }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="traumaTypeId">
        <Form.Label>Trauma Type</Form.Label>
        <Form.Select required value={formData.trauma_type_id || ""} onChange={(e) => onChange("trauma_type_id", e.target.value)}>
          <option value="">Select trauma type</option>
          {traumaTypes.map((tt) => (
            <option key={tt.trauma_type_id} value={tt.trauma_type_id}>{tt.trauma_type}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="traumaName">
        <Form.Label>Trauma Name</Form.Label>
        <Form.Control required type="text" placeholder="e.g. Fracture, Stab wound" value={formData.trauma_name || ""} onChange={(e) => onChange("trauma_name", e.target.value)} />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Addiction Category Master ───────────────────────────────────────────────────

const AddictionCategoryForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="addictionCategory">
      <Form.Label>Addiction Category</Form.Label>
      <Form.Control required type="text" placeholder="e.g. Substance, Behavioral" value={formData.addiction_category || ""} onChange={(e) => onChange("addiction_category", e.target.value)} />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Addiction Sub Category Master ───────────────────────────────────────────────

const AddictionSubCategoryForm = ({ formData, onChange, addictionCategories }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="addictionCategoryIdForSub">
        <Form.Label>Addiction Category</Form.Label>
        <Form.Select required value={formData.addiction_category_id || ""} onChange={(e) => onChange("addiction_category_id", e.target.value)}>
          <option value="">Select addiction category</option>
          {addictionCategories.map((ac) => (
            <option key={ac.addiction_category_id} value={ac.addiction_category_id}>{ac.addiction_category}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="addictionSubCategory">
        <Form.Label>Addiction Sub Category</Form.Label>
        <Form.Control required type="text" placeholder="e.g. Opioids, Stimulants" value={formData.addiction_sub_category || ""} onChange={(e) => onChange("addiction_sub_category", e.target.value)} />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Addiction Master ──────────────────────────────────────────────────────────

const AddictionsForm = ({ formData, onChange, addictionCategories, addictionSubCategories }) => {
  const filteredSubCategories = addictionSubCategories.filter(
    (sc) => String(sc.addiction_category_id) === String(formData.addiction_category_id)
  );
  const handleCategoryChange = (value) => {
    onChange("addiction_category_id", value);
  };

  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="addictionCategoryId">
          <Form.Label>Addiction Category</Form.Label>
          <Form.Select required value={formData.addiction_category_id || ""} onChange={(e) => handleCategoryChange(e.target.value)}>
            <option value="">Select addiction category</option>
            {addictionCategories.map((ac) => (
              <option key={ac.addiction_category_id} value={ac.addiction_category_id}>{ac.addiction_category}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="addictionSubCategoryId">
          <Form.Label>Addiction Sub Category</Form.Label>
          <Form.Select
            required
            value={formData.addiction_sub_category_id || ""}
            onChange={(e) => onChange("addiction_sub_category_id", e.target.value)}
            disabled={!formData.addiction_category_id}
          >
            <option value="">Select addiction sub category</option>
            {filteredSubCategories.map((sc) => (
              <option key={sc.addiction_sub_category_id} value={sc.addiction_sub_category_id}>{sc.addiction_sub_category}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="addictionsName">
          <Form.Label>Addiction</Form.Label>
          <Form.Control required type="text" placeholder="e.g. Alcohol, Tobacco" value={formData.addictions || ""} onChange={(e) => onChange("addictions", e.target.value)} />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="addictionsDescription">
          <Form.Label>Description</Form.Label>
          <Form.Control type="text" placeholder="Brief description" value={formData.description || ""} onChange={(e) => onChange("description", e.target.value)} />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="clinicalClassification">
          <Form.Label>Clinical Classification</Form.Label>
          <Form.Control type="text" placeholder="e.g. Substance Use Disorder" value={formData.clinical_classification || ""} onChange={(e) => onChange("clinical_classification", e.target.value)} />
        </Form.Group>
      </Row>
    </>
  );
};

// ─── Patient Condition Master ──────────────────────────────────────────────────

const PatientConditionForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="patientCondition">
      <Form.Label>Patient Condition</Form.Label>
      <Form.Control required type="text" placeholder="e.g. Stable, Critical, Recovering" value={formData.patient_condition || ""} onChange={(e) => onChange("patient_condition", e.target.value)} />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Investigation Type Master ─────────────────────────────────────────────────

const InvestigationTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="investigationType">
      <Form.Label>Investigation Type</Form.Label>
      <Form.Control required type="text" placeholder="e.g. Laboratory, Radiology" value={formData.investigation_type || ""} onChange={(e) => onChange("investigation_type", e.target.value)} />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Investigation Group Master ────────────────────────────────────────────────

const InvestigationGroupForm = ({ formData, onChange, investigationTypes }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="investigationTypeId">
        <Form.Label>Investigation Type</Form.Label>
        <Form.Select required value={formData.investigation_type_id || ""} onChange={(e) => onChange("investigation_type_id", e.target.value)}>
          <option value="">Select investigation type</option>
          {investigationTypes.map((it) => (
            <option key={it.investigation_type_id} value={it.investigation_type_id}>{it.investigation_type}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="investigationGroup">
        <Form.Label>Investigation Group</Form.Label>
        <Form.Control required type="text" placeholder="e.g. Hematology, Biochemistry" value={formData.investigation_group || ""} onChange={(e) => onChange("investigation_group", e.target.value)} />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Investigation Master ────────────────────────────────────────────────────────

const InvestigationForm = ({ formData, onChange, investigationTypes, investigationGroups, specialties }) => {
  const selectedSpecIds = formData.medical_specialty_ids
    ? formData.medical_specialty_ids.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  const handleSpecToggle = (id) => {
    const strId = String(id);
    const updated = selectedSpecIds.includes(strId)
      ? selectedSpecIds.filter((x) => x !== strId)
      : [...selectedSpecIds, strId];
    onChange("medical_specialty_ids", updated.join(","));
  };
  const filteredGroups = investigationGroups.filter(
    (g) => !formData.investigation_type_id || String(g.investigation_type_id) === String(formData.investigation_type_id)
  );
  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="invTypeId">
          <Form.Label>Investigation Type</Form.Label>
          <Form.Select required value={formData.investigation_type_id || ""} onChange={(e) => onChange("investigation_type_id", e.target.value)}>
            <option value="">Select investigation type</option>
            {investigationTypes.map((it) => (
              <option key={it.investigation_type_id} value={it.investigation_type_id}>{it.investigation_type}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="invGroupId">
          <Form.Label>Investigation Group <span className="text-muted">(optional)</span></Form.Label>
          <Form.Select value={formData.investigation_group_id || ""} onChange={(e) => onChange("investigation_group_id", e.target.value)}>
            <option value="">None</option>
            {filteredGroups.map((g) => (
              <option key={g.investigation_group_id} value={g.investigation_group_id}>{g.investigation_group}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="invSpecialties">
          <Form.Label>Medical Specialty (multiple)</Form.Label>
          <div className="d-flex flex-column gap-1 mt-1" style={{ maxHeight: 160, overflowY: "auto" }}>
            {specialties.map((s) => (
              <Form.Check
                key={s.medical_specialty_id}
                type="checkbox"
                id={`inv-spec-${s.medical_specialty_id}`}
                label={s.medical_specialty}
                checked={selectedSpecIds.includes(String(s.medical_specialty_id))}
                onChange={() => handleSpecToggle(s.medical_specialty_id)}
              />
            ))}
          </div>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="investigationName">
          <Form.Label>Investigation Name</Form.Label>
          <Form.Control required type="text" placeholder="e.g. Hemoglobin, Blood Glucose" value={formData.investigation_name || ""} onChange={(e) => onChange("investigation_name", e.target.value)} />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="measurementUnit">
          <Form.Label>Measurement Unit</Form.Label>
          <Form.Control type="text" placeholder="e.g. g/dL, mg/dL" value={formData.measurement_unit || ""} onChange={(e) => onChange("measurement_unit", e.target.value)} />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="3" controlId="normalLower">
          <Form.Label>Normal Range — Lower</Form.Label>
          <Form.Control type="number" step="any" value={formData.normal_range_lower ?? ""} onChange={(e) => onChange("normal_range_lower", e.target.value)} />
        </Form.Group>
        <Form.Group as={Col} md="3" controlId="normalUpper">
          <Form.Label>Normal Range — Upper</Form.Label>
          <Form.Control type="number" step="any" value={formData.normal_range_upper ?? ""} onChange={(e) => onChange("normal_range_upper", e.target.value)} />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="3" controlId="sosLower">
          <Form.Label>SOS Range — Lower</Form.Label>
          <Form.Control type="number" step="any" value={formData.sos_range_lower ?? ""} onChange={(e) => onChange("sos_range_lower", e.target.value)} />
        </Form.Group>
        <Form.Group as={Col} md="3" controlId="sosUpper">
          <Form.Label>SOS Range — Upper</Form.Label>
          <Form.Control type="number" step="any" value={formData.sos_range_upper ?? ""} onChange={(e) => onChange("sos_range_upper", e.target.value)} />
        </Form.Group>
      </Row>
    </>
  );
};

// ─── Establishment Type Master (establishment_type_master) ───────────────────

const EstablishmentTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="establishmentType">
      <Form.Label>Establishment Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Hospitals, Pharmacy, Pathology"
        value={formData.establishment_type || ""}
        onChange={(e) => onChange("establishment_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Establishment Sub Type Master (establishment_sub_type_master) ─────────────

const EstablishmentSubTypeForm = ({ formData, onChange, estTypes }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="estSubTypeEstTypeId">
        <Form.Label>Establishment Type ID</Form.Label>
        <Form.Select
          required
          value={formData.establishment_type_id || ""}
          onChange={(e) => onChange("establishment_type_id", e.target.value)}
        >
          <option value="">Select establishment type</option>
          {(Array.isArray(estTypes) ? estTypes : []).map((t) => (
            <option key={t.establishment_type_id} value={t.establishment_type_id}>
              {t.establishment_type}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="establishmentSubType">
        <Form.Label>Establishment Sub Type</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Single Specialty Hospital, Multi Specialty Hospital"
          value={formData.establishment_sub_type || ""}
          onChange={(e) => onChange("establishment_sub_type", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Medical Degree Master ───────────────────────────────────────────────────

const MedicalDegreeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="medicalDegreeTitle">
      <Form.Label>Medical Degree Title</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. MBBS, MD, MS"
        value={formData.medical_degree_title || ""}
        onChange={(e) => onChange("medical_degree_title", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Specialization Master ───────────────────────────────────────────────────

const SpecializationForm = ({ formData, onChange, specializationTypes }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="specializationTypeId">
        <Form.Label>Specialization Type</Form.Label>
        <Form.Select
          required
          value={formData.specialization_type_id || ""}
          onChange={(e) => onChange("specialization_type_id", e.target.value)}
        >
          <option value="">Select specialization type</option>
          {specializationTypes.map((st) => (
            <option key={st.specialization_type_id} value={st.specialization_type_id}>
              {st.specialization_type}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="specialization">
        <Form.Label>Specialization</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Cardiology, Neurology"
          value={formData.specialization || ""}
          onChange={(e) => onChange("specialization", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Specialization Type Master ──────────────────────────────────────────────

const SpecializationTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="specializationType">
      <Form.Label>Specialization Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Medical, Surgical, Diagnostic"
        value={formData.specialization_type || ""}
        onChange={(e) => onChange("specialization_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Accreditation Master ────────────────────────────────────────────────────

const AccreditationForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="accreditationTitle">
      <Form.Label>Accreditation Title</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. NABH, JCI, ISO"
        value={formData.accreditation_title || ""}
        onChange={(e) => onChange("accreditation_title", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Department Type Master (Hospital Departments) ───────────────────────────

const DepartmentCategoryForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="departmentCategory">
      <Form.Label>Department Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Clinical, Diagnostic, Support Services"
        value={formData.department_category || ""}
        onChange={(e) => onChange("department_category", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Hospital Department Master ──────────────────────────────────────────────

const DepartmentMasterForm = ({ formData, onChange, departmentCategories }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="departmentCategoryId">
        <Form.Label>Department Type</Form.Label>
        <Form.Select
          required
          value={formData.department_category_id || ""}
          onChange={(e) => onChange("department_category_id", e.target.value)}
        >
          <option value="">Select department type</option>
          {departmentCategories.map((dc) => (
            <option key={dc.department_category_id} value={dc.department_category_id}>
              {dc.department_category}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="departmentName">
        <Form.Label>Hospital Department</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Cardiology, Radiology, Emergency"
          value={formData.department_name || ""}
          onChange={(e) => onChange("department_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Designation Master ──────────────────────────────────────────────────────

const DesignationForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="designation">
      <Form.Label>Designation</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Chief Medical Officer, Consultant"
        value={formData.designation || ""}
        onChange={(e) => onChange("designation", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Relationship Master ─────────────────────────────────────────────────────

const RelationshipForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="relationship">
      <Form.Label>Relationship</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Father, Mother, Spouse, Son, Daughter"
        value={formData.relationship || ""}
        onChange={(e) => onChange("relationship", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Blood Group Master ────────────────────────────────────────────────────────

const BloodGroupForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="bloodGroup">
      <Form.Label>Blood Group</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. A+, B+, O+, AB+, A-, B-, O-, AB-"
        value={formData.blood_group || ""}
        onChange={(e) => onChange("blood_group", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Therapeutic Class Master ────────────────────────────────────────────────────

const TherapeuticClassForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="therapeuticClass">
      <Form.Label>Therapeutic Class</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Analgesics, Antibiotics, Antihypertensives"
        value={formData.therapeutic_class || ""}
        onChange={(e) => onChange("therapeutic_class", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Medicine Salt Master ────────────────────────────────────────────────────────

const MedicineSaltForm = ({ formData, onChange, therapeuticClasses }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="therapeuticClassId">
        <Form.Label>Therapeutic Class</Form.Label>
        <Form.Select
          required
          value={formData.therapeutic_class_id || ""}
          onChange={(e) => onChange("therapeutic_class_id", e.target.value)}
        >
          <option value="">Select therapeutic class</option>
          {therapeuticClasses.map((tc) => (
            <option key={tc.therapeutic_class_id} value={tc.therapeutic_class_id}>
              {tc.therapeutic_class}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="saltName">
        <Form.Label>Salt Name</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Paracetamol, Amoxicillin"
          value={formData.salt_name || ""}
          onChange={(e) => onChange("salt_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="chemicalFormula">
        <Form.Label>Chemical Formula</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. C8H9NO2"
          value={formData.chemical_formula || ""}
          onChange={(e) => onChange("chemical_formula", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="saltDescription">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Brief description of the salt"
          value={formData.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="saltSchedule">
        <Form.Label>Salt Schedule</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Schedule H, Schedule H1, OTC"
          value={formData.salt_schedule || ""}
          onChange={(e) => onChange("salt_schedule", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

// ─── Medical Prescription Master ─────────────────────────────────────────────────

const appendPrescriptionFormData = (fd, data, includeExisting = false) => {
  fd.append("case_id", data.case_id || "");
  fd.append("parent_case_id", data.parent_case_id || "");
  fd.append("patient_id", data.patient_id || "");
  fd.append("prescription_date", data.prescription_date || "");
  fd.append("doctor_id", data.doctor_id || "");
  fd.append("establishment_id", data.establishment_id || "");
  fd.append("duration_days", data.duration_days ?? "");
  if (data.prescription_image_front instanceof File) {
    fd.append("prescription_image_front", data.prescription_image_front);
  }
  if (data.prescription_image_back instanceof File) {
    fd.append("prescription_image_back", data.prescription_image_back);
  }
  if (includeExisting) {
    if (data.prescription_image_front && !(data.prescription_image_front instanceof File)) {
      fd.append("existing_prescription_image_front", data.prescription_image_front);
    }
    if (data.prescription_image_back && !(data.prescription_image_back instanceof File)) {
      fd.append("existing_prescription_image_back", data.prescription_image_back);
    }
  }
};

const MedicalPrescriptionForm = ({
  formData,
  onChange,
  patients,
  patientCases,
  doctors,
  establishments,
}) => {
  const filteredCases = patientCases.filter(
    (c) => !formData.patient_id || String(c.patient_id) === String(formData.patient_id)
  );
  const filteredParentCases = filteredCases.filter(
    (c) => !formData.case_id || String(c.case_id) !== String(formData.case_id)
  );

  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="prescriptionPatientId">
          <Form.Label>Patient</Form.Label>
          <Form.Select
            required
            value={formData.patient_id || ""}
            onChange={(e) => onChange("patient_id", e.target.value)}
          >
            <option value="">Select patient</option>
            {patients.map((p) => (
              <option key={p.patient_id} value={p.patient_id}>
                {p.patient_name || `Patient #${p.patient_id}`}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="prescriptionCaseId">
          <Form.Label>Case ID</Form.Label>
          <Form.Select
            required
            value={formData.case_id || ""}
            onChange={(e) => onChange("case_id", e.target.value)}
            disabled={!formData.patient_id}
          >
            <option value="">Select case</option>
            {filteredCases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                Case #{c.case_id} — {c.case_date_time ? new Date(c.case_date_time).toLocaleString() : "—"}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="prescriptionParentCaseId">
          <Form.Label>Parent Case ID</Form.Label>
          <Form.Select
            value={formData.parent_case_id || ""}
            onChange={(e) => onChange("parent_case_id", e.target.value)}
            disabled={!formData.patient_id}
          >
            <option value="">None</option>
            {filteredParentCases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                Case #{c.case_id} — {c.case_date_time ? new Date(c.case_date_time).toLocaleString() : "—"}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="prescriptionDate">
          <Form.Label>Date</Form.Label>
          <Form.Control
            required
            type="date"
            value={formData.prescription_date || ""}
            onChange={(e) => onChange("prescription_date", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="prescriptionDurationDays">
          <Form.Label>Duration (days)</Form.Label>
          <Form.Control
            type="number"
            min="1"
            placeholder="e.g. 7, 14, 30"
            value={formData.duration_days ?? ""}
            onChange={(e) => onChange("duration_days", e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="prescriptionDoctorId">
          <Form.Label>Doctor</Form.Label>
          <Form.Select
            required
            value={formData.doctor_id || ""}
            onChange={(e) => onChange("doctor_id", e.target.value)}
          >
            <option value="">Select doctor</option>
            {doctors.map((d) => (
              <option key={d.doctor_id} value={d.doctor_id}>
                {d.doctor_name || `Doctor #${d.doctor_id}`}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="prescriptionEstablishmentId">
          <Form.Label>Establishment</Form.Label>
          <Form.Select
            required
            value={formData.establishment_id || ""}
            onChange={(e) => onChange("establishment_id", e.target.value)}
          >
            <option value="">Select establishment</option>
            {establishments.map((e) => (
              <option key={e.establishment_id} value={e.establishment_id}>
                {e.establishment_name || `Establishment #${e.establishment_id}`}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="prescriptionImageFront">
          <Form.Label>Prescription Image Front</Form.Label>
          <Form.Control
            type="file"
            accept="image/*"
            onChange={(e) => onChange("prescription_image_front", e.target.files[0])}
          />
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="prescriptionImageBack">
          <Form.Label>Prescription Image Back</Form.Label>
          <Form.Control
            type="file"
            accept="image/*"
            onChange={(e) => onChange("prescription_image_back", e.target.files[0])}
          />
        </Form.Group>
      </Row>
    </>
  );
};

// ─── Dosage Master ─────────────────────────────────────────────────────────────

const DosageForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="dosageType">
      <Form.Label>Dosage Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. O.D., B.D., T.D.S., S.O.S."
        value={formData.dosage_type || ""}
        onChange={(e) => onChange("dosage_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Patient Prescription Medicine Master ────────────────────────────────────────

const PatientPrescriptionMedicineForm = ({
  formData,
  onChange,
  patients,
  patientCases,
  medicalPrescriptions,
  medicineSalts,
  medicineDosages,
}) => {
  const filteredCases = patientCases.filter(
    (c) => !formData.patient_id || String(c.patient_id) === String(formData.patient_id)
  );
  const filteredPrescriptions = medicalPrescriptions.filter(
    (rx) => !formData.case_id || String(rx.case_id) === String(formData.case_id)
  );

  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="ppmPatientId">
          <Form.Label>Patient</Form.Label>
          <Form.Select
            required
            value={formData.patient_id || ""}
            onChange={(e) => onChange("patient_id", e.target.value)}
          >
            <option value="">Select patient</option>
            {patients.map((p) => (
              <option key={p.patient_id} value={p.patient_id}>
                {p.patient_name || `Patient #${p.patient_id}`}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="ppmCaseId">
          <Form.Label>Case ID</Form.Label>
          <Form.Select
            required
            value={formData.case_id || ""}
            onChange={(e) => onChange("case_id", e.target.value)}
            disabled={!formData.patient_id}
          >
            <option value="">Select case</option>
            {filteredCases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                Case #{c.case_id} — {c.case_date_time ? new Date(c.case_date_time).toLocaleString() : "—"}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="ppmPrescriptionId">
          <Form.Label>Prescription ID</Form.Label>
          <Form.Select
            required
            value={formData.prescription_id || ""}
            onChange={(e) => onChange("prescription_id", e.target.value)}
            disabled={!formData.case_id}
          >
            <option value="">Select prescription</option>
            {filteredPrescriptions.map((rx) => (
              <option key={rx.prescription_id} value={rx.prescription_id}>
                Prescription #{rx.prescription_id}
                {rx.prescription_date ? ` — ${String(rx.prescription_date).slice(0, 10)}` : ""}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="ppmSaltId">
          <Form.Label>Salt</Form.Label>
          <Form.Select
            required
            value={formData.salt_id || ""}
            onChange={(e) => onChange("salt_id", e.target.value)}
          >
            <option value="">Select salt</option>
            {medicineSalts.map((ms) => (
              <option key={ms.salt_id} value={ms.salt_id}>
                {ms.salt_name}
                {ms.therapeutic_class ? ` (${ms.therapeutic_class})` : ""}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="ppmDosageId">
          <Form.Label>Dosage</Form.Label>
          <Form.Select
            required
            value={formData.dosage_id || ""}
            onChange={(e) => onChange("dosage_id", e.target.value)}
          >
            <option value="">Select dosage</option>
            {medicineDosages.map((d) => (
              <option key={d.dosage_id} value={d.dosage_id}>
                {d.dosage_type}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="ppmDurationDays">
          <Form.Label>Duration (days)</Form.Label>
          <Form.Control
            type="number"
            min="0"
            placeholder="e.g. 5, 7, 10"
            value={formData.duration_days ?? ""}
            onChange={(e) => onChange("duration_days", e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="ppmIsEmptyStomach">
          <Form.Label>Is Empty Stomach</Form.Label>
          <YesNoRadioInline
            name="ppmIsEmptyStomach"
            value={formData.is_empty_stomach ?? 0}
            onChange={(v) => onChange("is_empty_stomach", v)}
          />
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="ppmIsSos">
          <Form.Label>Is SOS</Form.Label>
          <YesNoRadioInline
            name="ppmIsSos"
            value={formData.is_sos ?? 0}
            onChange={(v) => onChange("is_sos", v)}
          />
        </Form.Group>
      </Row>
    </>
  );
};

// ─── Patient Case Master ───────────────────────────────────────────────────────

const PatientCaseForm = ({ formData, onChange, patients, doctors, specialties, medicalServices }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="pcPatientId">
        <Form.Label>Patient ID</Form.Label>
        <Form.Select
          required
          value={formData.patient_id || ""}
          onChange={(e) => onChange("patient_id", e.target.value)}
        >
          <option value="">Select patient</option>
          {patients.map((p) => (
            <option key={p.patient_id} value={p.patient_id}>
              {p.patient_id} — {p.patient_name || "Patient"}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="pcReferringDoctorId">
        <Form.Label>Referring Doctor ID</Form.Label>
        <Form.Select
          value={formData.referring_doctor_id || ""}
          onChange={(e) => onChange("referring_doctor_id", e.target.value)}
        >
          <option value="">Select doctor (optional)</option>
          {doctors.map((d) => (
            <option key={d.doctor_id} value={d.doctor_id}>
              {d.doctor_id} — {d.doctor_name || "Doctor"}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="pcMedicalSpecialtyId">
        <Form.Label>Medical Specialty ID</Form.Label>
        <Form.Select
          value={formData.medical_specialty_id || ""}
          onChange={(e) => onChange("medical_specialty_id", e.target.value)}
        >
          <option value="">Select medical specialty (optional)</option>
          {specialties.map((s) => (
            <option key={s.medical_specialty_id} value={s.medical_specialty_id}>
              {s.medical_specialty_id} — {s.medical_specialty}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="pcMedicalServiceId">
        <Form.Label>Medical Service Type</Form.Label>
        <Form.Select
          value={formData.medical_service_id || ""}
          onChange={(e) => onChange("medical_service_id", e.target.value)}
        >
          <option value="">Select medical service (optional)</option>
          {medicalServices.map((ms) => (
            <option key={ms.medical_service_id} value={ms.medical_service_id}>
              {ms.medical_service_id} — {ms.medical_service}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="pcCaseDateTime">
        <Form.Label>Date &amp; Time</Form.Label>
        <Form.Control
          required
          type="datetime-local"
          value={formData.case_date_time || ""}
          onChange={(e) => onChange("case_date_time", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="12" controlId="pcComments">
        <Form.Label>Comments</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          placeholder="Case comments"
          value={formData.comments || ""}
          onChange={(e) => onChange("comments", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

// ─── Patient Complaints ────────────────────────────────────────────────────────

const PatientComplaintForm = ({
  formData,
  onChange,
  patients,
  patientCases,
  symptoms,
  aggravatingFactors,
}) => {
  const filteredCases = patientCases.filter(
    (c) => !formData.patient_id || String(c.patient_id) === String(formData.patient_id)
  );

  const handleAggravatingChange = (e) => {
    const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
    onChange("aggravating_factor_ids", selected);
  };

  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="pcomplPatientId">
          <Form.Label>Patient ID</Form.Label>
          <Form.Select
            required
            value={formData.patient_id || ""}
            onChange={(e) => onChange("patient_id", e.target.value)}
          >
            <option value="">Select patient</option>
            {patients.map((p) => (
              <option key={p.patient_id} value={p.patient_id}>
                {p.patient_id} — {p.patient_name || "Patient"}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="pcomplCaseId">
          <Form.Label>Case ID</Form.Label>
          <Form.Select
            required
            value={formData.case_id || ""}
            onChange={(e) => onChange("case_id", e.target.value)}
            disabled={!formData.patient_id}
          >
            <option value="">Select case</option>
            {filteredCases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                Case #{c.case_id}
                {c.case_date_time ? ` — ${new Date(c.case_date_time).toLocaleString()}` : ""}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="pcomplSymptomId">
          <Form.Label>Complaint (Symptom Master)</Form.Label>
          <Form.Select
            value={formData.symptom_id || ""}
            onChange={(e) => {
              const symptomId = e.target.value;
              onChange("symptom_id", symptomId);
              const symptom = symptoms.find(
                (s) => String(s.symptom_id) === String(symptomId)
              );
              if (symptom?.symptom) onChange("complaint", symptom.symptom);
            }}
          >
            <option value="">Select symptom (optional)</option>
            {symptoms.map((s) => (
              <option key={s.symptom_id} value={s.symptom_id}>
                {s.symptom}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="pcomplComplaint">
          <Form.Label>Complaint Text</Form.Label>
          <Form.Control
            required
            type="text"
            placeholder="Complaint description"
            value={formData.complaint || ""}
            onChange={(e) => onChange("complaint", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="pcomplSeverity">
          <Form.Label>Severity (1–10)</Form.Label>
          <Form.Select
            value={formData.severity ?? ""}
            onChange={(e) => onChange("severity", e.target.value)}
          >
            <option value="">Select severity</option>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="pcomplIsSos">
          <Form.Label>Is SOS</Form.Label>
          <YesNoRadioInline
            name="pcomplIsSos"
            value={formData.is_sos ? 1 : 0}
            onChange={(v) => onChange("is_sos", Boolean(v))}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="12" controlId="pcomplAggravating">
          <Form.Label>Aggravating Factors</Form.Label>
          <Form.Select
            multiple
            value={(formData.aggravating_factor_ids || []).map(String)}
            onChange={handleAggravatingChange}
            style={{ minHeight: 120 }}
          >
            {aggravatingFactors.map((af) => (
              <option key={af.aggravating_factor_id} value={af.aggravating_factor_id}>
                {af.aggravating_factor}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">Hold Ctrl/Cmd to select multiple.</Form.Text>
        </Form.Group>
      </Row>
    </>
  );
};

// ─── Case Diagnosis ────────────────────────────────────────────────────────────

const CaseDiagnosisForm = ({ formData, onChange, patients, patientCases, diseases }) => {
  const filteredCases = patientCases.filter(
    (c) => !formData.patient_id || String(c.patient_id) === String(formData.patient_id)
  );

  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="cdPatientId">
          <Form.Label>Patient ID</Form.Label>
          <Form.Select
            required
            value={formData.patient_id || ""}
            onChange={(e) => onChange("patient_id", e.target.value)}
          >
            <option value="">Select patient</option>
            {patients.map((p) => (
              <option key={p.patient_id} value={p.patient_id}>
                {p.patient_id} — {p.patient_name || "Patient"}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="cdCaseId">
          <Form.Label>Case ID</Form.Label>
          <Form.Select
            required
            value={formData.case_id || ""}
            onChange={(e) => onChange("case_id", e.target.value)}
            disabled={!formData.patient_id}
          >
            <option value="">Select case</option>
            {filteredCases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                Case #{c.case_id}
                {c.case_date_time ? ` — ${new Date(c.case_date_time).toLocaleString()}` : ""}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="cdDiseaseId">
          <Form.Label>Diagnosis ID (Disease Master)</Form.Label>
          <Form.Select
            value={formData.disease_id || ""}
            onChange={(e) => {
              const diseaseId = e.target.value;
              onChange("disease_id", diseaseId);
              const disease = diseases.find(
                (d) => String(d.disease_id) === String(diseaseId)
              );
              if (disease?.disease_name) onChange("diagnosis", disease.disease_name);
            }}
          >
            <option value="">Select disease (optional)</option>
            {diseases.map((d) => (
              <option key={d.disease_id} value={d.disease_id}>
                {d.disease_id} — {d.disease_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="cdDiagnosis">
          <Form.Label>Diagnosis</Form.Label>
          <Form.Control
            required
            type="text"
            placeholder="Diagnosis text"
            value={formData.diagnosis || ""}
            onChange={(e) => onChange("diagnosis", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="cdStatus">
          <Form.Label>Status</Form.Label>
          <Form.Select
            value={formData.status || "Provisional"}
            onChange={(e) => onChange("status", e.target.value)}
          >
            <option value="Provisional">Provisional</option>
            <option value="Confirmed">Confirmed</option>
          </Form.Select>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="12" controlId="cdComments">
          <Form.Label>Comments</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Comments"
            value={formData.comments || ""}
            onChange={(e) => onChange("comments", e.target.value)}
          />
        </Form.Group>
      </Row>
    </>
  );
};

// ─── Patient Investigation ─────────────────────────────────────────────────────

const appendInvestigationFormData = (fd, data, includeExisting = false) => {
  if (data.case_diagnosis_id) fd.append("case_diagnosis_id", data.case_diagnosis_id);
  if (data.investigation_id) fd.append("investigation_id", data.investigation_id);
  if (data.investigation_date) fd.append("investigation_date", data.investigation_date);
  if (data.comments) fd.append("comments", data.comments);
  fd.append("abnormality_ids", JSON.stringify(data.abnormality_ids || []));
  fd.append(
    "abnormalities",
    JSON.stringify(
      data.abnormalities
        || (data.abnormalities_text
          ? data.abnormalities_text.split(",").map((item) => item.trim()).filter(Boolean)
          : [])
    )
  );
  if (data.upload_scan instanceof File) {
    fd.append("upload_scan", data.upload_scan);
  }
  if (data.upload_report instanceof File) {
    fd.append("upload_report", data.upload_report);
  }
  if (includeExisting) {
    if (data.upload_scan_path && !(data.upload_scan instanceof File)) {
      fd.append("existing_upload_scan_path", data.upload_scan_path);
    }
    if (data.upload_report_path && !(data.upload_report instanceof File)) {
      fd.append("existing_upload_report_path", data.upload_report_path);
    }
  }
};

const PatientInvestigationForm = ({
  formData,
  onChange,
  patients,
  patientCases,
  caseDiagnoses,
  investigations,
}) => {
  const filteredCases = patientCases.filter(
    (c) => !formData.patient_id || String(c.patient_id) === String(formData.patient_id)
  );
  const filteredDiagnoses = caseDiagnoses.filter(
    (d) => !formData.case_id || String(d.case_id) === String(formData.case_id)
  );

  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="piPatientId">
          <Form.Label>Patient ID</Form.Label>
          <Form.Select
            required
            value={formData.patient_id || ""}
            onChange={(e) => onChange("patient_id", e.target.value)}
          >
            <option value="">Select patient</option>
            {patients.map((p) => (
              <option key={p.patient_id} value={p.patient_id}>
                {p.patient_id} — {p.patient_name || "Patient"}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="piCaseId">
          <Form.Label>Case ID</Form.Label>
          <Form.Select
            required
            value={formData.case_id || ""}
            onChange={(e) => onChange("case_id", e.target.value)}
            disabled={!formData.patient_id}
          >
            <option value="">Select case</option>
            {filteredCases.map((c) => (
              <option key={c.case_id} value={c.case_id}>
                Case #{c.case_id}
                {c.case_date_time ? ` — ${new Date(c.case_date_time).toLocaleString()}` : ""}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="piDiagnosisId">
          <Form.Label>Diagnosis ID</Form.Label>
          <Form.Select
            value={formData.case_diagnosis_id || ""}
            onChange={(e) => onChange("case_diagnosis_id", e.target.value)}
            disabled={!formData.case_id}
          >
            <option value="">Select diagnosis (optional)</option>
            {filteredDiagnoses.map((d) => (
              <option key={d.case_diagnosis_id} value={d.case_diagnosis_id}>
                {d.case_diagnosis_id} — {d.diagnosis || d.disease_name || "Diagnosis"}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="piInvestigationId">
          <Form.Label>Investigation ID (Investigation Master)</Form.Label>
          <Form.Select
            value={formData.investigation_id || ""}
            onChange={(e) => onChange("investigation_id", e.target.value)}
          >
            <option value="">Select investigation (optional)</option>
            {investigations.map((inv) => (
              <option key={inv.investigation_id} value={inv.investigation_id}>
                {inv.investigation_id} — {inv.investigation_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="piUploadScan">
          <Form.Label>Upload Scan</Form.Label>
          {formData.upload_scan_path && !(formData.upload_scan instanceof File) ? (
            <div className="mb-2">
              <a href={`${UPLOADS_BASE}/uploads/${formData.upload_scan_path}`} target="_blank" rel="noreferrer">
                <img
                  src={`${UPLOADS_BASE}/uploads/${formData.upload_scan_path}`}
                  alt="Scan"
                  style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 4 }}
                />
              </a>
            </div>
          ) : null}
          <Form.Control
            type="file"
            accept="image/*"
            onChange={(e) => onChange("upload_scan", e.target.files[0])}
          />
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="piUploadReport">
          <Form.Label>Upload Report</Form.Label>
          {formData.upload_report_path && !(formData.upload_report instanceof File) ? (
            <div className="mb-2">
              <a href={`${UPLOADS_BASE}/uploads/${formData.upload_report_path}`} target="_blank" rel="noreferrer">
                <img
                  src={`${UPLOADS_BASE}/uploads/${formData.upload_report_path}`}
                  alt="Report"
                  style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 4 }}
                />
              </a>
            </div>
          ) : null}
          <Form.Control
            type="file"
            accept="image/*"
            onChange={(e) => onChange("upload_report", e.target.files[0])}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="piInvestigationDate">
          <Form.Label>Investigation Date</Form.Label>
          <Form.Control
            type="date"
            value={formData.investigation_date || ""}
            onChange={(e) => onChange("investigation_date", e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="piAbnormalities">
          <Form.Label>Abnormalities (comma-separated)</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Elevated WBC, Low hemoglobin"
            value={formData.abnormalities_text || ""}
            onChange={(e) => onChange("abnormalities_text", e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="12" controlId="piComments">
          <Form.Label>Comments</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Comments"
            value={formData.comments || ""}
            onChange={(e) => onChange("comments", e.target.value)}
          />
        </Form.Group>
      </Row>
    </>
  );
};

// ─── Profile Topic Master ──────────────────────────────────────────────────────

const ProfileTopicForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="profileTopic">
      <Form.Label>Profile Topic</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Medical History, Lifestyle, Family History"
        value={formData.profile_topic || ""}
        onChange={(e) => onChange("profile_topic", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Profile Sub Topic Master ──────────────────────────────────────────────────

const ProfileSubTopicForm = ({ formData, onChange, profileTopics }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="profileTopicId">
        <Form.Label>Profile Topic</Form.Label>
        <Form.Select
          required
          value={formData.profile_topic_id || ""}
          onChange={(e) => onChange("profile_topic_id", e.target.value)}
        >
          <option value="">Select profile topic</option>
          {profileTopics.map((pt) => (
            <option key={pt.profile_topic_id} value={pt.profile_topic_id}>
              {pt.profile_topic}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="profileSubTopic">
        <Form.Label>Profile Sub Topic</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Past Surgeries, Diet, Exercise"
          value={formData.profile_sub_topic || ""}
          onChange={(e) => onChange("profile_sub_topic", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Medical Service Master ────────────────────────────────────────────────────

const MedicalServiceForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="medicalService">
      <Form.Label>Medical Service</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Consultation, Surgery, Diagnostic"
        value={formData.medical_service || ""}
        onChange={(e) => onChange("medical_service", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Service Type Master ───────────────────────────────────────────────────────

const ServiceTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="serviceType">
      <Form.Label>Service Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Consultation, Diagnostic, Surgical"
        value={formData.service_type || ""}
        onChange={(e) => onChange("service_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Service Master ────────────────────────────────────────────────────────────

const ServiceForm = ({ formData, onChange, serviceTypes }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="serviceTypeId">
        <Form.Label>Service Type</Form.Label>
        <Form.Select
          required
          value={formData.service_type_id || ""}
          onChange={(e) => onChange("service_type_id", e.target.value)}
        >
          <option value="">Select service type</option>
          {serviceTypes.map((st) => (
            <option key={st.service_type_id} value={st.service_type_id}>
              {st.service_type}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="serviceTitle">
        <Form.Label>Service Title</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. General Consultation, Blood Test Panel"
          value={formData.service_title || ""}
          onChange={(e) => onChange("service_title", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="serviceDescription">
        <Form.Label>Description</Form.Label>
        <Form.Control
          type="text"
          placeholder="Brief description of the service"
          value={formData.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

// ─── Question Type Master ──────────────────────────────────────────────────────

const QuestionTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="questionType">
      <Form.Label>Question Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Multiple Choice, Yes/No, Text"
        value={formData.question_type || ""}
        onChange={(e) => onChange("question_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Question Master ───────────────────────────────────────────────────────────

const QUESTION_LINKING_OPTIONS = [
  { value: "profile_topic", label: "Profile Topic" },
  { value: "profile_sub_topic", label: "Profile Sub Topic" },
  { value: "service", label: "Service" },
];

const QUESTION_LINKING_LABELS = Object.fromEntries(
  QUESTION_LINKING_OPTIONS.map((o) => [o.value, o.label])
);

const getQuestionLinkDisplay = (q) => {
  if (q.question_linking === "profile_topic") return q.profile_topic || "—";
  if (q.question_linking === "profile_sub_topic") return q.profile_sub_topic || "—";
  if (q.question_linking === "service") return q.service_title || "—";
  return "—";
};

const appendQuestionFormData = (fd, data, includeExisting = false) => {
  fd.append("question_linking", data.question_linking || "");
  fd.append("profile_topic_id", data.profile_topic_id || "");
  fd.append("profile_sub_topic_id", data.profile_sub_topic_id || "");
  fd.append("service_id", data.service_id || "");
  fd.append("question_type_id", data.question_type_id);
  fd.append("question_text", data.question_text);
  fd.append("description", data.description || data.question_text || "");
  fd.append("video_url", data.video_url || "");
  if (includeExisting) {
    if (data.related_image_paths) fd.append("existing_related_image_paths", data.related_image_paths);
    if (data.video_path) fd.append("existing_video_path", data.video_path);
  }
  if (data.related_images?.length) {
    Array.from(data.related_images).forEach((file) => fd.append("related_images", file));
  }
  if (data.video instanceof File) fd.append("video", data.video);
};

const QuestionForm = ({
  formData, onChange, questionTypes, profileTopics, profileSubTopics, services,
}) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="questionLinking">
        <Form.Label>Question Linking</Form.Label>
        <Form.Select
          required
          value={formData.question_linking || ""}
          onChange={(e) => onChange("question_linking", e.target.value)}
        >
          <option value="">Select linking type</option>
          {QUESTION_LINKING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    {formData.question_linking === "profile_topic" && (
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="profileTopicId">
          <Form.Label>Profile Topic</Form.Label>
          <Form.Select
            required
            value={formData.profile_topic_id || ""}
            onChange={(e) => onChange("profile_topic_id", e.target.value)}
          >
            <option value="">Select profile topic</option>
            {profileTopics.map((pt) => (
              <option key={pt.profile_topic_id} value={pt.profile_topic_id}>
                {pt.profile_topic}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
    )}
    {formData.question_linking === "profile_sub_topic" && (
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="profileSubTopicId">
          <Form.Label>Profile Sub Topic</Form.Label>
          <Form.Select
            required
            value={formData.profile_sub_topic_id || ""}
            onChange={(e) => onChange("profile_sub_topic_id", e.target.value)}
          >
            <option value="">Select profile sub topic</option>
            {profileSubTopics.map((pst) => (
              <option key={pst.profile_sub_topic_id} value={pst.profile_sub_topic_id}>
                {pst.profile_sub_topic}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
    )}
    {formData.question_linking === "service" && (
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="serviceId">
          <Form.Label>Service</Form.Label>
          <Form.Select
            required
            value={formData.service_id || ""}
            onChange={(e) => onChange("service_id", e.target.value)}
          >
            <option value="">Select service</option>
            {services.map((s) => (
              <option key={s.service_id} value={s.service_id}>{s.service_title}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
    )}
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="questionTypeId">
        <Form.Label>Question Type</Form.Label>
        <Form.Select
          required
          value={formData.question_type_id || ""}
          onChange={(e) => onChange("question_type_id", e.target.value)}
        >
          <option value="">Select question type</option>
          {questionTypes.map((qt) => (
            <option key={qt.question_type_id} value={qt.question_type_id}>{qt.question_type}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="questionText">
        <Form.Label>Question Text</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. How often do you exercise per week?"
          value={formData.question_text || ""}
          onChange={(e) => onChange("question_text", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="questionDescription">
        <Form.Label>Description</Form.Label>
        <Form.Control
          type="text"
          placeholder="Defaults to question text"
          value={formData.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="relatedImages">
        <Form.Label>Related Image (optional)</Form.Label>
        <Form.Control
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => onChange("related_images", e.target.files)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="relatedVideo">
        <Form.Label>Related Video (optional)</Form.Label>
        <Form.Control
          type="file"
          accept="video/*"
          onChange={(e) => onChange("video", e.target.files[0])}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="videoUrl">
        <Form.Label>Video URL (optional)</Form.Label>
        <Form.Control
          type="url"
          placeholder="https://example.com/video.mp4"
          value={formData.video_url || ""}
          onChange={(e) => onChange("video_url", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

const SEQUENCE_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

const MASTER_TABLE_REFERENCES = [
  { value: "user_type_master", label: "User Type Master" },
  { value: "location_type_master", label: "Location Type Master" },
  { value: "location_master", label: "Location Master" },
  { value: "medical_specialty_master", label: "Medical Specialty Master" },
  { value: "medical_service_master", label: "Medical Services Master" },
  { value: "medical_procedure_master", label: "Medical Procedure Master" },
  { value: "disease_master", label: "Disease Master" },
  { value: "symptom_category_master", label: "Symptom Category Master" },
  { value: "symptom_master", label: "Symptom Master" },
  { value: "aggravating_factor_master", label: "Aggravating Factor Master" },
  { value: "allergy_type_master", label: "Allergy Type Master" },
  { value: "allergy_master", label: "Allergy Master" },
  { value: "trauma_type_master", label: "Trauma Type Master" },
  { value: "trauma_master", label: "Trauma Master" },
  { value: "addiction_category_master", label: "Addiction Category Master" },
  { value: "addiction_sub_category_master", label: "Addiction Sub Category Master" },
  { value: "addictions_master", label: "Addiction Master" },
  { value: "patient_condition_master", label: "Patient Condition Master" },
  { value: "investigation_type_master", label: "Investigation Type Master" },
  { value: "investigation_group_master", label: "Investigation Group Master" },
  { value: "investigation_master", label: "Investigation Master" },
  { value: "product_category_master", label: "Product Category Master" },
  { value: "product_sub_category_master", label: "Product Sub Category Master" },
  { value: "product_master", label: "Product Master" },
  { value: "supplier_type_master", label: "Supplier Type Master" },
  { value: "establishment_type_master", label: "Establishment Type Master" },
  { value: "establishment_sub_type_master", label: "Establishment Sub Type Master" },
  { value: "medical_degree_master", label: "Medical Degree Master" },
  { value: "specialization_type_master", label: "Specialization Type Master" },
  { value: "specialization_master", label: "Specialization Master" },
  { value: "accreditation_master", label: "Accreditation Master" },
  { value: "establishment_master", label: "Establishment Master" },
  { value: "doctor_master", label: "Doctor Master" },
  { value: "login_master", label: "Login Master" },
  { value: "department_category_master", label: "Department Type Master" },
  { value: "department_master", label: "Hospital Department Master" },
  { value: "designation_master", label: "Designation Master" },
  { value: "relationship_master", label: "Relationship Master" },
  { value: "blood_group_master", label: "Blood Group Master" },
  { value: "therapeutic_class_master", label: "Therapeutic Class Master" },
  { value: "medicine_salt_master", label: "Medicine Salt Master" },
  { value: "dosage_master", label: "Dosage Master" },
  { value: "medical_prescription_master", label: "Medical Prescription Master" },
  { value: "patient_prescription_medicine_master", label: "Patient Prescription Medicine Master" },
  { value: "patient_master", label: "Patient Master" },
  { value: "profile_topic_master", label: "Profile Topic Master" },
  { value: "profile_sub_topic_master", label: "Profile Sub Topic Master" },
  { value: "service_type_master", label: "Service Type Master" },
  { value: "service_master", label: "Service Master" },
  { value: "question_type_master", label: "Question Type Master" },
  { value: "question_group_master", label: "Question Group Master" },
  { value: "question_master", label: "Question Master" },
  { value: "option_master", label: "Option Master" },
  { value: "event_type_master", label: "Event Type Master" },
  { value: "immunization_master", label: "Immunization Master" },
  { value: "vaccine_master", label: "Vaccine Master" },
  { value: "login_log_master", label: "Login Log" },
];

const IMMUNIZATION_TYPE_OPTIONS = ["Single Dose", "Multiple Dose"];
const APPLICABLE_GENDER_OPTIONS = ["Male", "Female", "Both"];
const VACCINE_GENDER_OPTIONS = ["All", "Female", "Male", "Both"];
const APPLICABLE_AGE_GROUP_OPTIONS = ["Child", "Adolescent", "Adult", "Senior"];

const getMorbidityDiseases = (diseases) =>
  diseases.filter((d) => Number(d.comorbidity_disease) === 1);

const formatTableReference = (value) =>
  MASTER_TABLE_REFERENCES.find((t) => t.value === value)?.label || value || "—";

// ─── Option Master ─────────────────────────────────────────────────────────────

const OptionForm = ({ formData, onChange, questions }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="questionId">
        <Form.Label>Question</Form.Label>
        <Form.Select
          required
          value={formData.question_id || ""}
          onChange={(e) => onChange("question_id", e.target.value)}
        >
          <option value="">Select question</option>
          {questions.map((q) => (
            <option key={q.question_id} value={q.question_id}>
              {q.question_text}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="3" controlId="sequence">
        <Form.Label>Sequence</Form.Label>
        <Form.Select
          required
          value={formData.sequence || ""}
          onChange={(e) => onChange("sequence", e.target.value)}
        >
          <option value="">Select</option>
          {SEQUENCE_OPTIONS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="3" controlId="weightage">
        <Form.Label>Weightage</Form.Label>
        <Form.Control
          required
          type="number"
          min="1"
          max="10"
          placeholder="1-10"
          value={formData.weightage ?? ""}
          onChange={(e) => onChange("weightage", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required (1-10)</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="optionText">
        <Form.Label>Option Text</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Never, Sometimes, Daily"
          value={formData.option_text || ""}
          onChange={(e) => onChange("option_text", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="tableReference">
        <Form.Label>Table Reference</Form.Label>
        <Form.Select
          value={formData.table_reference || ""}
          onChange={(e) => onChange("table_reference", e.target.value)}
        >
          <option value="">Select master table</option>
          {MASTER_TABLE_REFERENCES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </Form.Select>
      </Form.Group>
    </Row>
  </>
);

// ─── Question Group Master ─────────────────────────────────────────────────────

const QuestionGroupForm = ({ formData, onChange, profileSubTopics, services }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="profileSubTopicId">
        <Form.Label>Profile Sub Topic</Form.Label>
        <Form.Select
          required
          value={formData.profile_sub_topic_id || ""}
          onChange={(e) => onChange("profile_sub_topic_id", e.target.value)}
        >
          <option value="">Select profile sub topic</option>
          {profileSubTopics.map((pst) => (
            <option key={pst.profile_sub_topic_id} value={pst.profile_sub_topic_id}>
              {pst.profile_sub_topic}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="serviceId">
        <Form.Label>Service</Form.Label>
        <Form.Select
          required
          value={formData.service_id || ""}
          onChange={(e) => onChange("service_id", e.target.value)}
        >
          <option value="">Select service</option>
          {services.map((s) => (
            <option key={s.service_id} value={s.service_id}>
              {s.service_title}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="questionGroup">
        <Form.Label>Question Group</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Diet Assessment Questions"
          value={formData.question_group || ""}
          onChange={(e) => onChange("question_group", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Event Type Master ─────────────────────────────────────────────────────────

const EventTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="eventType">
      <Form.Label>Event Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Appointment, Follow-up, Emergency Visit"
        value={formData.event_type || ""}
        onChange={(e) => onChange("event_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

// ─── Immunization Master ───────────────────────────────────────────────────────

const ImmunizationForm = ({ formData, onChange, morbidityDiseases }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="immunizationName">
        <Form.Label>Immunization Name</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Hepatitis B, MMR, Influenza"
          value={formData.immunization_name || ""}
          onChange={(e) => onChange("immunization_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="immunizationType">
        <Form.Label>Immunization Type</Form.Label>
        <Form.Select
          required
          value={formData.immunization_type || ""}
          onChange={(e) => onChange("immunization_type", e.target.value)}
        >
          <option value="">Select type</option>
          {IMMUNIZATION_TYPE_OPTIONS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="applicableGender">
        <Form.Label>Applicable Gender</Form.Label>
        <Form.Select
          required
          value={formData.applicable_gender || ""}
          onChange={(e) => onChange("applicable_gender", e.target.value)}
        >
          <option value="">Select gender</option>
          {APPLICABLE_GENDER_OPTIONS.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="applicableAgeGroup">
        <Form.Label>Applicable Age Group</Form.Label>
        <Form.Select
          required
          value={formData.applicable_age_group || ""}
          onChange={(e) => onChange("applicable_age_group", e.target.value)}
        >
          <option value="">Select age group</option>
          {APPLICABLE_AGE_GROUP_OPTIONS.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="applicableMorbidity">
        <Form.Label>Applicable Morbidity</Form.Label>
        <Form.Select
          required
          value={formData.applicable_morbidity_id || ""}
          onChange={(e) => onChange("applicable_morbidity_id", e.target.value)}
        >
          <option value="">Select disease (comorbidity = Yes)</option>
          {morbidityDiseases.map((d) => (
            <option key={d.disease_id} value={d.disease_id}>
              {d.disease_name}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Vaccine Master ────────────────────────────────────────────────────────────

const VaccineForm = ({ formData, onChange }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="vaccineAgeGroup">
        <Form.Label>Age Group</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Infancy (Birth-12 mo)"
          value={formData.age_group || ""}
          onChange={(e) => onChange("age_group", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="vaccineGender">
        <Form.Label>Gender</Form.Label>
        <Form.Select
          required
          value={formData.gender || ""}
          onChange={(e) => onChange("gender", e.target.value)}
        >
          <option value="">Select gender</option>
          {VACCINE_GENDER_OPTIONS.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="vaccineName">
        <Form.Label>Vaccine Name</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Hepatitis B (HepB)"
          value={formData.vaccine_name || ""}
          onChange={(e) => onChange("vaccine_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="scheduleDetail">
        <Form.Label>Schedule Detail</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. 3-dose series (Birth, 1-2 mos, 6-18 mos)"
          value={formData.schedule_detail || ""}
          onChange={(e) => onChange("schedule_detail", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
  </>
);

// ─── Establishment Master ─────────────────────────────────────────────────────

const CATEGORY_OPTIONS = [
  "General", "Nursing Home", "Single Speciality",
  "Multi-Speciality", "Center of Excellence", "Medical College",
];

/** Normalize establishment type rows for dropdowns. */
const normalizeEstablishmentTypes = (estTypes) =>
  (Array.isArray(estTypes) ? estTypes : [])
    .map((t) => ({
      id: t.establishment_type_id,
      name: t.establishment_type,
    }))
    .filter((t) => t.id != null && t.name);

/** Normalize establishment sub type rows for dropdowns. */
const normalizeEstablishmentSubTypes = (subTypes) =>
  (Array.isArray(subTypes) ? subTypes : [])
    .map((t) => ({
      id: t.establishment_sub_type_id,
      name: t.establishment_sub_type,
      establishment_type_id: t.establishment_type_id,
    }))
    .filter((t) => t.id != null && t.name);

/** Sub types linked to the selected establishment type. */
const filterEstablishmentSubTypes = (subTypes, establishmentTypeId) => {
  if (!establishmentTypeId) return [];
  return normalizeEstablishmentSubTypes(subTypes).filter(
    (t) => String(t.establishment_type_id) === String(establishmentTypeId)
  );
};

const EstSectionCard = ({ title, sectionNumber, children }) => (
  <div className="em-section-card">
    {title && (
      <h4 className="em-section-title">
        {sectionNumber != null && <span className="em-section-num">{sectionNumber}. </span>}
        {title}
      </h4>
    )}
    {children}
  </div>
);

const EstablishmentMasterForm = ({ formData, onChange, establishments, estTypes, establishmentSubTypes }) => {
  const establishmentTypes = normalizeEstablishmentTypes(estTypes);
  const subTypeOptions = filterEstablishmentSubTypes(establishmentSubTypes, formData.establishment_type_id);

  const handleEstablishmentTypeChange = (value) => {
    onChange("establishment_type_id", value);
    onChange("establishment_sub_type_id", "");
  };

  return (
  <div className="em-form">
    <EstSectionCard title="Basic Information" sectionNumber={1}>
      <Row className="em-row">
        <Form.Group as={Col} md="6" controlId="estTypeId">
          <Form.Label>Establishment Type <span className="text-danger">*</span></Form.Label>
          <Form.Select
            required
            value={formData.establishment_type_id || ""}
            onChange={(e) => handleEstablishmentTypeChange(e.target.value)}
          >
            <option value="">Select establishment type</option>
            {establishmentTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            {establishmentTypes.length === 0 && (
              <option value="" disabled>Add establishment types in Establishment Type Master first</option>
            )}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="establishmentSubTypeId">
          <Form.Label>Establishment Sub Type</Form.Label>
          <Form.Select
            value={formData.establishment_sub_type_id || ""}
            onChange={(e) => onChange("establishment_sub_type_id", e.target.value)}
          >
            <option value="">Select establishment sub type</option>
            {subTypeOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
            {subTypeOptions.length === 0 && (
              <option value="" disabled>Add sub types in Establishment Sub Type Master first</option>
            )}
          </Form.Select>
        </Form.Group>
      </Row>

      <Row className="em-row">
        <Form.Group as={Col} md="6" controlId="brandName">
          <Form.Label>Brand Name <span className="text-danger">*</span></Form.Label>
          <Form.Control
            required
            type="text"
            placeholder="e.g. Apollo Hospitals"
            value={formData.establishment_name || ""}
            onChange={(e) => onChange("establishment_name", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="legalEntityName">
          <Form.Label>Legal Entity Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Registered legal entity name"
            value={formData.legal_entity_name || ""}
            onChange={(e) => onChange("legal_entity_name", e.target.value)}
          />
        </Form.Group>
      </Row>

      <Row className="em-row em-row--last">
        <Form.Group as={Col} md="6" controlId="estYear">
          <Form.Label>Establishment Year</Form.Label>
          <Form.Control
            type="number"
            placeholder="e.g. 1995"
            min="1800"
            max={new Date().getFullYear()}
            value={formData.establishment_year || ""}
            onChange={(e) => onChange("establishment_year", e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="parentEstId">
          <Form.Label>Parent Entity</Form.Label>
          <Form.Select
            value={formData.parent_establishment_id || ""}
            onChange={(e) => onChange("parent_establishment_id", e.target.value)}
          >
            <option value="">None (Top-level)</option>
            {establishments.map((est) => (
              <option key={est.establishment_id} value={est.establishment_id}>
                {est.establishment_name}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
    </EstSectionCard>
  </div>
  );
};


// ─── Supplier / Product Masters ───────────────────────────────────────────────

const PRODUCT_STATUS_OPTIONS = ["Active", "Discontinued", "Prohibited"];

const SupplierTypeForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="supplierType">
      <Form.Label>Supplier Type</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Manufacturer, Distributor, Wholesaler"
        value={formData.supplier_type || ""}
        onChange={(e) => onChange("supplier_type", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

const PRODUCT_CATEGORY_GROUP_OPTIONS = [
  "Implants",
  "Medical Devices",
  "Equipment",
  "Consumables",
  "Hospital Essentials",
  "Pharmaceuticals",
  "Software",
];

const PRODUCT_MASTER_STATUS_OPTIONS = ["Active", "Inactive"];

const ProductCategoryForm = ({ formData, onChange }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="productCategoryCode">
        <Form.Label>Category ID / Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. CAT-01"
          value={formData.category_code || ""}
          onChange={(e) => onChange("category_code", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="productCategory">
        <Form.Label>Product Category</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Orthopedic Implants, Cardiovascular Devices"
          value={formData.product_category || ""}
          onChange={(e) => onChange("product_category", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="productCategoryGroup">
        <Form.Label>Category Group</Form.Label>
        <Form.Select
          value={formData.category_group || ""}
          onChange={(e) => onChange("category_group", e.target.value)}
        >
          <option value="">Select category group</option>
          {PRODUCT_CATEGORY_GROUP_OPTIONS.map((group) => (
            <option key={group} value={group}>{group}</option>
          ))}
        </Form.Select>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="productCategoryStatus">
        <Form.Label>Status</Form.Label>
        <Form.Select
          value={formData.status || "Active"}
          onChange={(e) => onChange("status", e.target.value)}
        >
          {PRODUCT_MASTER_STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </Form.Select>
      </Form.Group>
    </Row>
  </>
);

const ProductSubCategoryForm = ({ formData, onChange, productCategories }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="productCategoryIdForSub">
        <Form.Label>Product Category</Form.Label>
        <Form.Select
          required
          value={formData.product_category_id || ""}
          onChange={(e) => onChange("product_category_id", e.target.value)}
        >
          <option value="">Select product category</option>
          {productCategories.map((pc) => (
            <option key={pc.product_category_id} value={pc.product_category_id}>
              {pc.category_code ? `${pc.category_code} — ${pc.product_category}` : pc.product_category}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="productSubCategoryCode">
        <Form.Label>Sub Category ID / Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. SUBCAT-0001"
          value={formData.sub_category_code || ""}
          onChange={(e) => onChange("sub_category_code", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="productSubCategory">
        <Form.Label>Product Sub Category</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Hip Implants, Coronary Stents"
          value={formData.product_sub_category || ""}
          onChange={(e) => onChange("product_sub_category", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="productSubCategoryStatus">
        <Form.Label>Status</Form.Label>
        <Form.Select
          value={formData.status || "Active"}
          onChange={(e) => onChange("status", e.target.value)}
        >
          {PRODUCT_MASTER_STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </Form.Select>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="12" controlId="productSubCategoryDescription">
        <Form.Label>Sub Category Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          placeholder="Describe the sub category"
          value={formData.sub_category_description || ""}
          onChange={(e) => onChange("sub_category_description", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="productSubCategoryHsn">
        <Form.Label>Indicative HSN / HS Code</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. 90212100"
          value={formData.hsn_code || ""}
          onChange={(e) => onChange("hsn_code", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="productSubCategoryRegulatory">
        <Form.Label>Applicable Regulatory Body</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. CDSCO / US FDA / CE"
          value={formData.regulatory_body || ""}
          onChange={(e) => onChange("regulatory_body", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

const MvtJourneyPhaseForm = ({ formData, onChange }) => (
  <Row className="mb-4">
    <Form.Group as={Col} md="6" controlId="mvtJourneyPhase">
      <Form.Label>MVT Journey Phase</Form.Label>
      <Form.Control
        required
        type="text"
        placeholder="e.g. Enquiry, Evaluation, Treatment, Follow-up"
        value={formData.mvt_journey_phase || ""}
        onChange={(e) => onChange("mvt_journey_phase", e.target.value)}
      />
      <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
    </Form.Group>
  </Row>
);

const MvtJourneyStepForm = ({ formData, onChange, mvtJourneyPhases }) => (
  <>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="mvtJourneyPhaseIdForStep">
        <Form.Label>MVT Journey Phase</Form.Label>
        <Form.Select
          required
          value={formData.mvt_journey_phase_id || ""}
          onChange={(e) => onChange("mvt_journey_phase_id", e.target.value)}
        >
          <option value="">Select journey phase</option>
          {mvtJourneyPhases.map((p) => (
            <option key={p.mvt_journey_phase_id} value={p.mvt_journey_phase_id}>
              {p.mvt_journey_phase}
            </option>
          ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="mvtJourneyStepName">
        <Form.Label>MVT Journey Step Name</Form.Label>
        <Form.Control
          required
          type="text"
          placeholder="e.g. Share Medical Reports"
          value={formData.mvt_journey_step_name || ""}
          onChange={(e) => onChange("mvt_journey_step_name", e.target.value)}
        />
        <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="mvtJourneyStepTat">
        <Form.Label>TAT</Form.Label>
        <Form.Control
          type="number"
          min="0"
          placeholder="Turnaround time"
          value={formData.tat ?? ""}
          onChange={(e) => onChange("tat", e.target.value)}
        />
      </Form.Group>
      <Form.Group as={Col} md="6" controlId="mvtJourneyStepInputType">
        <Form.Label>Input Type</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Document, Image, Text"
          value={formData.input_type || ""}
          onChange={(e) => onChange("input_type", e.target.value)}
        />
      </Form.Group>
    </Row>
    <Row className="mb-4">
      <Form.Group as={Col} md="6" controlId="mvtJourneyStepOutputType">
        <Form.Label>Output Type</Form.Label>
        <Form.Control
          type="text"
          placeholder="e.g. Report, Approval, Quote"
          value={formData.output_type || ""}
          onChange={(e) => onChange("output_type", e.target.value)}
        />
      </Form.Group>
    </Row>
  </>
);

const appendProductFormData = (fd, data) => {
  fd.append("product_category_id", data.product_category_id || "");
  fd.append("product_sub_category_id", data.product_sub_category_id || "");
  fd.append("product_name", data.product_name || "");
  fd.append("oem_id", data.oem_id || "");
  fd.append("medical_specialty_ids", data.medical_specialty_ids || "");
  fd.append("estimated_price_inr", data.estimated_price_inr ?? "");
  fd.append("estimated_price_usd", data.estimated_price_usd ?? "");
  fd.append("short_description", data.short_description || "");
  fd.append("long_description", data.long_description || "");
  fd.append("product_video_url", data.product_video_url || "");
  fd.append("comments", data.comments || "");
  fd.append("status", data.status || "Active");
  if (data.removed_product_image_ids?.length) {
    fd.append("removed_product_image_ids", JSON.stringify(data.removed_product_image_ids));
  }
  const imageFiles = Array.isArray(data.product_image_files) ? data.product_image_files : [];
  imageFiles.forEach((file) => {
    if (file instanceof File) fd.append("product_images", file);
  });
  if (data.product_profile instanceof File) {
    fd.append("product_profile", data.product_profile);
  }
};

const ProductMasterForm = ({ formData, onChange, productCategories, productSubCategories, specialties = [] }) => {
  const filteredSubCategories = productSubCategories.filter(
    (sc) => !formData.product_category_id
      || String(sc.product_category_id) === String(formData.product_category_id)
  );
  const selectedSpecIds = formData.medical_specialty_ids
    ? formData.medical_specialty_ids.toString().split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  const handleSpecToggle = (id) => {
    const strId = String(id);
    const updated = selectedSpecIds.includes(strId)
      ? selectedSpecIds.filter((x) => x !== strId)
      : [...selectedSpecIds, strId];
    onChange("medical_specialty_ids", updated.join(","));
  };

  return (
    <>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="productCategoryId">
          <Form.Label>Product Category</Form.Label>
          <Form.Select
            required
            value={formData.product_category_id || ""}
            onChange={(e) => onChange("product_category_id", e.target.value)}
          >
            <option value="">Select product category</option>
            {productCategories.map((pc) => (
              <option key={pc.product_category_id} value={pc.product_category_id}>
                {pc.product_category}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="productSubCategoryId">
          <Form.Label>Product Sub Category</Form.Label>
          <Form.Select
            required
            value={formData.product_sub_category_id || ""}
            onChange={(e) => onChange("product_sub_category_id", e.target.value)}
            disabled={!formData.product_category_id}
          >
            <option value="">Select product sub category</option>
            {filteredSubCategories.map((sc) => (
              <option key={sc.product_sub_category_id} value={sc.product_sub_category_id}>
                {sc.product_sub_category}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="productName">
          <Form.Label>Product Name</Form.Label>
          <Form.Control
            required
            type="text"
            placeholder="Enter product name"
            value={formData.product_name || ""}
            onChange={(e) => onChange("product_name", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="oemId">
          <Form.Label>OEM ID</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. OEM-12345"
            value={formData.oem_id || ""}
            onChange={(e) => onChange("oem_id", e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="productMedicalSpecialtyIds">
          <Form.Label>Medical Specialty ID (multiple)</Form.Label>
          <div className="master-checkbox-list">
            {specialties.map((s) => (
              <Form.Check
                key={s.medical_specialty_id}
                type="checkbox"
                id={`product-spec-${s.medical_specialty_id}`}
                label={s.medical_specialty}
                checked={selectedSpecIds.includes(String(s.medical_specialty_id))}
                onChange={() => handleSpecToggle(s.medical_specialty_id)}
              />
            ))}
          </div>
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="estimatedPriceInr">
          <Form.Label>Estimated Price in INR</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            placeholder="e.g. 150000"
            value={formData.estimated_price_inr ?? ""}
            onChange={(e) => onChange("estimated_price_inr", e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="estimatedPriceUsd">
          <Form.Label>Estimated Price in USD</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            placeholder="e.g. 1800"
            value={formData.estimated_price_usd ?? ""}
            onChange={(e) => onChange("estimated_price_usd", e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="productStatus">
          <Form.Label>Status</Form.Label>
          <Form.Select
            required
            value={formData.status || "Active"}
            onChange={(e) => onChange("status", e.target.value)}
          >
            {PRODUCT_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="productVideoUrl">
          <Form.Label>Product Video (link)</Form.Label>
          <Form.Control
            type="url"
            placeholder="https://youtube.com/..."
            value={formData.product_video_url || ""}
            onChange={(e) => onChange("product_video_url", e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="shortDescription">
          <Form.Label>Short Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Brief product summary"
            value={formData.short_description || ""}
            onChange={(e) => onChange("short_description", e.target.value)}
          />
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="longDescription">
          <Form.Label>Long Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Detailed product description"
            value={formData.long_description || ""}
            onChange={(e) => onChange("long_description", e.target.value)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="6" controlId="productImages">
          <Form.Label>Product Images</Form.Label>
          <Form.Control
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => onChange("product_image_files", Array.from(e.target.files || []))}
          />
          <span className="em-field-hint">You can select multiple images</span>
        </Form.Group>
        <Form.Group as={Col} md="6" controlId="productProfile">
          <Form.Label>Product Profile (PDF)</Form.Label>
          <Form.Control
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => onChange("product_profile", e.target.files?.[0] || null)}
          />
        </Form.Group>
      </Row>
      <Row className="mb-4">
        <Form.Group as={Col} md="12" controlId="productComments">
          <Form.Label>Comments</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Internal notes or comments"
            value={formData.comments || ""}
            onChange={(e) => onChange("comments", e.target.value)}
          />
        </Form.Group>
      </Row>
    </>
  );
};


export {
  SEQUENCE_OPTIONS,
  MASTER_TABLE_REFERENCES,
  formatTableReference,
  getQuestionLinkDisplay,
  IMMUNIZATION_TYPE_OPTIONS,
  APPLICABLE_GENDER_OPTIONS,
  VACCINE_GENDER_OPTIONS,
  APPLICABLE_AGE_GROUP_OPTIONS,
  CATEGORY_OPTIONS,
  normalizeEstablishmentTypes,
  normalizeEstablishmentSubTypes,
  filterEstablishmentSubTypes,
  YesNoRadioInline,
  formatYesNo,
  QUESTION_LINKING_OPTIONS,
  QUESTION_LINKING_LABELS,
  UserTypeMasterForm,
  LocationTypeMasterForm,
  LocationMasterForm,
  MedicalSpecialtyForm,
  MedicalProcedureForm,
  DiseaseForm,
  SymptomCategoryForm,
  SymptomForm,
  AggravatingFactorForm,
  AllergyTypeForm,
  AllergyForm,
  TraumaTypeForm,
  TraumaForm,
  AddictionCategoryForm,
  AddictionSubCategoryForm,
  AddictionsForm,
  PatientConditionForm,
  InvestigationTypeForm,
  InvestigationGroupForm,
  InvestigationForm,
  EstablishmentTypeForm,
  EstablishmentSubTypeForm,
  MedicalDegreeForm,
  SpecializationForm,
  SpecializationTypeForm,
  AccreditationForm,
  DepartmentCategoryForm,
  DepartmentMasterForm,
  DesignationForm,
  RelationshipForm,
  BloodGroupForm,
  TherapeuticClassForm,
  MedicineSaltForm,
  DosageForm,
  MedicalPrescriptionForm,
  PatientPrescriptionMedicineForm,
  PatientCaseForm,
  PatientComplaintForm,
  CaseDiagnosisForm,
  PatientInvestigationForm,
  ProfileTopicForm,
  ProfileSubTopicForm,
  ServiceTypeForm,
  MedicalServiceForm,
  ServiceForm,
  QuestionTypeForm,
  QuestionForm,
  OptionForm,
  QuestionGroupForm,
  EventTypeForm,
  ImmunizationForm,
  VaccineForm,
  EstablishmentMasterForm,
  SupplierTypeForm,
  ProductCategoryForm,
  ProductSubCategoryForm,
  ProductMasterForm,
  MvtJourneyPhaseForm,
  MvtJourneyStepForm,
  PRODUCT_STATUS_OPTIONS,
  appendProductFormData,
  getMorbidityDiseases,
  appendQuestionFormData,
  appendPrescriptionFormData,
  appendInvestigationFormData,
};
